import { WEBAPP_URL } from '@/lib/constants';
import { getSessionManager } from '@/lib/SessionTracking/SessionManager';

export const isBrowser = () => typeof window !== 'undefined';

export function isCharacterALetter(char: string) {
  return /[a-zA-Z]/.test(char);
}

//get month and day
export const getMonthAndDay = () => {
  const date = new Date();
  const month = date.toLocaleString('default', { month: 'long' });
  const day = date.getDate();
  return `${month} ${day}`;
};

export const getMinDateOfCurrentYear = () => {
  // get format of 2021-01-01
  const date = new Date();
  const year = date.getFullYear();
  return `${year}-01-01`;
};

export const isUrl = (str: string) => {
  return /^https?:\/\//gi.test(str);
};

// remove day from start and end date
export const removeDayFromDate = (date: string) => {
  const trimmedDate = date.split('T')[0];
  const [year, month] = trimmedDate.split('-');
  return `${year}/${month}`;
};

const toRelativePath = (url: string) => {
  const parsed = new URL(url);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const appendSessionIdToUrl = (
  url: string,
  sessionId?: string
): string => {
  try {
    const shouldPreserveRelativeServerHref =
      typeof window === 'undefined' &&
      url.startsWith('/') &&
      !url.startsWith('//');
    const resolved = new URL(
      url,
      typeof window !== 'undefined' ? window.location.origin : WEBAPP_URL
    );
    const sid =
      sessionId ||
      (typeof window !== 'undefined'
        ? getSessionManager()?.getSessionId()
        : undefined);
    if (sid) {
      resolved.searchParams.set('sessionId', sid);
    }
    const href = resolved.toString();
    return shouldPreserveRelativeServerHref ? toRelativePath(href) : href;
  } catch {
    return url;
  }
};

export const generateUniversalLink = ({
  assignmentId,
  studentId,
  sessionId,
  replay,
  challenge,
  publicOrigin,
}: {
  assignmentId?: string;
  studentId?: string;
  sessionId?: string;
  replay?: boolean;
  challenge?: string;
  publicOrigin?: string;
}) => {
  const shouldUseRelativeServerHref =
    !publicOrigin && typeof window === 'undefined';
  const constructedUniversalLink = new URL(
    '/api/game-services/open',
    publicOrigin ??
      (typeof window !== 'undefined' ? window.location.origin : WEBAPP_URL)
  );
  assignmentId &&
    constructedUniversalLink.searchParams.append('assignmentId', assignmentId);
  studentId &&
    constructedUniversalLink.searchParams.append('studentId', studentId);
  replay && constructedUniversalLink.searchParams.append('replay', 'true');

  challenge &&
    constructedUniversalLink.searchParams.append('challenge', challenge);

  const href = appendSessionIdToUrl(
    constructedUniversalLink.toString(),
    sessionId
  );
  return shouldUseRelativeServerHref ? toRelativePath(href) : href;
};

const gradeLevelDict: { [key: string]: string } = {
  first: '1st',
  second: '2nd',
  third: '3rd',
  fourth: '4th',
  fifth: '5th',
  sixth: '6th',
  seventh: '7th',
  eighth: '8th',
  ninth: '9th',
};

export const gradeLevelTranslator = (gradeLevel: string) => {
  return gradeLevelDict[gradeLevel.toLowerCase()] || gradeLevel;
};

export const gradeNumberToEnum = (gradeLevel: string) => {
  switch (gradeLevel) {
    case 'k':
      return 'K';
    case '1':
      return 'FIRST';
    case '2':
      return 'SECOND';
    case '3':
      return 'THIRD';
    case '4':
      return 'FOURTH';
    case '5':
      return 'FIFTH';
    case '6':
      return 'SIXTH';
    case '7':
      return 'SEVENTH';
    case '8':
      return 'EIGHTH';

    default:
      return 'MULTIPLE';
  }
};

export {
  challengeCodeDict,
  getChallengeSortOrder,
  translateChallengeCodeToName,
} from './challenges';

export const scrollToContent = (id: string) => {
  const element = document.getElementById(id);
  element?.scrollIntoView({ behavior: 'smooth' });
};
